import { RemovalBehavior, type UpdateFrequency } from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  cn,
  Input,
  Label,
  Select,
  SelectItem,
  Switch,
  toast,
} from '@utima/ui';
import { Info, RefreshCw, Trash2 } from 'lucide-react';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import { LockedFeatureIcon } from '@/components';
import { Alert } from '@/components/Alert';
import { SimpleTooltip } from '@/components/tooltips/simple-tooltip';
import { useEnumTranslations } from '@/hooks';
import { useSubscriptionFeatures } from '@/hooks/useSubscriptionFeatures';
import { invalidateKB } from '@/services/invalidateKB';
import { trpc } from '@/services/trpc';

import type { AddSourcePanelHandle } from './addSourcePanelHandle';
import { UPDATE_FREQUENCY_OPTIONS } from '../../lib/updateFrequencyOptions';

const INHERIT_UPDATE_FREQUENCY = '__inherit__' as const;

export type DriveFolderFormProps = {
  chatIds: string[];
  availableForAllAgents: boolean;
  setOpen: (open: boolean) => void;
  defaultUrl?: string;
  limitReached: boolean;
  folderId?: string | null;
  /** Renders as a settings-only panel: no own URL input and no own
   * Add/submit button — the shared "Online sources" row owns the URL and
   * drives submission via the imperative handle (Phase R6). */
  embedded?: boolean;
  onSubmittingChange?: (isSubmitting: boolean) => void;
};

export const AddDriveFolder = memo(
  forwardRef<AddSourcePanelHandle, DriveFolderFormProps>(
    function DriveFolderForm(
      {
        chatIds,
        availableForAllAgents,
        setOpen,
        defaultUrl,
        limitReached,
        folderId,
        embedded = false,
        onSubmittingChange,
      },
      ref,
    ) {
      const { t } = useLingui();
      const enumTranslations = useEnumTranslations();
      const { isKbAutoUpdatesLocked } = useSubscriptionFeatures();

      const [shareLink, setShareLink] = useState(defaultUrl ?? '');

      useEffect(() => {
        if (defaultUrl) {
          setShareLink(defaultUrl);
        }
      }, [defaultUrl]);
      const [includeSubfolders, setIncludeSubfolders] = useState(true);
      const [updateFrequency, setUpdateFrequency] =
        useState<UpdateFrequency | null>(null);
      const [removalBehavior, setRemovalBehavior] = useState<RemovalBehavior>(
        RemovalBehavior.Flag,
      );

      const createMutation = trpc.documentFolders.createDriveFolder.useMutation(
        {
          onSuccess: async result => {
            await invalidateKB();

            if (!result.initialSync.ok) {
              toast.warning(
                t`Google Drive folder connected, but the initial sync failed. Use Sync now to retry.`,
              );
              setOpen(false);

              return;
            }

            const {
              filesCreated,
              filesSkipped = 0,
              filesFailed = 0,
            } = result.initialSync.payload;
            const message = t`Google Drive folder connected. Imported: ${filesCreated}; skipped: ${filesSkipped}; failed: ${filesFailed}. Imported files are queued for processing.`;

            if (filesFailed > 0 || filesSkipped > 0) {
              toast.warning(message);
            } else {
              toast.success(message);
            }

            setOpen(false);
          },
          onError: error => {
            toast.error(error.message);
          },
        },
      );

      const handleCreate = useCallback(() => {
        if (!shareLink.trim()) {
          return;
        }

        createMutation.mutate({
          shareLink: shareLink.trim(),
          includeSubfolders,
          updateFrequency,
          removalBehavior,
          availableForAllAgents,
          chatIds,
          parentId: folderId ?? null,
        });
      }, [
        shareLink,
        includeSubfolders,
        updateFrequency,
        removalBehavior,
        availableForAllAgents,
        chatIds,
        folderId,
        createMutation,
      ]);

      useImperativeHandle(ref, () => ({ submit: handleCreate }), [
        handleCreate,
      ]);

      useEffect(() => {
        onSubmittingChange?.(createMutation.isPending);
      }, [createMutation.isPending, onSubmittingChange]);

      return (
        <>
          {limitReached && (
            <Alert
              variant='warning'
              title={t`Document Limit Reached`}
              className='mb-4'
            >
              <Trans>
                You have reached the maximum document limit for your plan.
              </Trans>
            </Alert>
          )}

          <div className='flex h-full max-h-[500px] min-h-0 grow flex-col space-y-2'>
            {!embedded && (
              <div className='flex flex-col gap-2 sm:flex-row sm:items-end'>
                <div className='min-w-0 flex-1 space-y-1'>
                  <Label htmlFor='drive-share-link'>
                    <Trans>Folder share link</Trans>
                  </Label>
                  <Input
                    id='drive-share-link'
                    data-cta='admin-add-drive-folder-link-input'
                    placeholder='https://drive.google.com/drive/folders/...'
                    value={shareLink}
                    onChange={e => setShareLink(e.target.value)}
                  />
                </div>
                <Button
                  data-cta='admin-add-drive-folder-create-button'
                  disabled={
                    !shareLink.trim() ||
                    limitReached ||
                    createMutation.isPending
                  }
                  loading={createMutation.isPending}
                  onClick={handleCreate}
                >
                  <Trans>Connect folder</Trans>
                </Button>
              </div>
            )}

            <details className='shrink-0 rounded-lg bg-muted/30 px-3 py-2'>
              <summary className='cursor-pointer text-xs text-muted-fg'>
                <Trans>Advanced settings</Trans>
              </summary>
              <div className='mt-3 grid grid-cols-1 gap-3 md:grid-cols-3'>
                <div className='flex flex-col justify-between gap-2 min-w-0'>
                  <div className='flex items-center gap-2'>
                    <Label className='text-xs font-medium'>
                      <Trans>Include subfolders</Trans>
                    </Label>
                    <SimpleTooltip
                      delayDuration={0}
                      title={t`When enabled, files in nested subfolders are synced too.`}
                    >
                      <Info className='size-3 shrink-0 cursor-help text-muted-fg' />
                    </SimpleTooltip>
                  </div>
                  <Switch
                    aria-label={t`Include subfolders`}
                    checked={includeSubfolders}
                    onCheckedChange={setIncludeSubfolders}
                  />
                </div>

                <div
                  className={cn(
                    'min-w-0',
                    isKbAutoUpdatesLocked && 'opacity-50',
                  )}
                >
                  <div className='mb-2 flex items-center gap-2'>
                    <RefreshCw className='size-3.5 shrink-0 text-muted-fg' />
                    <Label className='text-xs font-medium'>
                      <Trans>Auto Refresh</Trans>
                    </Label>
                    <LockedFeatureIcon locked={isKbAutoUpdatesLocked} />
                    <SimpleTooltip
                      title={t`How often the folder is re-scanned to check for updates.`}
                    >
                      <Info className='size-3 shrink-0 cursor-help text-muted-fg' />
                    </SimpleTooltip>
                  </div>
                  <div
                    className={cn(
                      isKbAutoUpdatesLocked && 'pointer-events-none',
                    )}
                  >
                    <Select
                      value={updateFrequency ?? INHERIT_UPDATE_FREQUENCY}
                      disabled={isKbAutoUpdatesLocked}
                      onValueChange={v =>
                        setUpdateFrequency(
                          v === INHERIT_UPDATE_FREQUENCY
                            ? null
                            : (v as UpdateFrequency),
                        )
                      }
                    >
                      <SelectItem value={INHERIT_UPDATE_FREQUENCY}>
                        <Trans>Inherit</Trans>
                      </SelectItem>
                      {UPDATE_FREQUENCY_OPTIONS.map(freq => (
                        <SelectItem key={freq} value={freq}>
                          {enumTranslations.updateFrequency[freq]}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>
                </div>

                <div
                  className={cn(
                    'min-w-0',
                    isKbAutoUpdatesLocked && 'opacity-50',
                  )}
                >
                  <div className='mb-2 flex items-center gap-2'>
                    <Trash2 className='size-3.5 shrink-0 text-muted-fg' />
                    <Label className='text-xs font-medium'>
                      <Trans>If Source Removed</Trans>
                    </Label>
                    <LockedFeatureIcon locked={isKbAutoUpdatesLocked} />
                    <SimpleTooltip
                      title={t`What happens when the source file is no longer available. Flag marks it for review, Remove deletes it automatically.`}
                    >
                      <Info className='size-3 shrink-0 cursor-help text-muted-fg' />
                    </SimpleTooltip>
                  </div>
                  <div
                    className={cn(
                      isKbAutoUpdatesLocked && 'pointer-events-none',
                    )}
                  >
                    <Select
                      value={removalBehavior}
                      disabled={isKbAutoUpdatesLocked}
                      onValueChange={v =>
                        setRemovalBehavior(v as RemovalBehavior)
                      }
                    >
                      {Object.values(RemovalBehavior).map(behavior => (
                        <SelectItem key={behavior} value={behavior}>
                          {enumTranslations.removalBehavior[behavior]}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>
                </div>
              </div>
            </details>
          </div>
        </>
      );
    },
  ),
);
